import React, {
  useEffect,
  createContext,
  useCallback,
  useState,
  useMemo
} from 'react';
import PropTypes from 'prop-types';

import { gtagEvent } from 'src/utils/gtag';
import useRestApiClient from 'src/hooks/useRestApiClient';

const initialState = {
  mySnippets: null,
  filteredMySnippets: [],
  isLoading: false,
  isPreloaded: false,
  filterValues: { search_title: '' },
  setFilterValues: () => Promise.resolve(),
  updateSnippetLocal: () => {}
};

const MySnippetsContext = createContext(initialState);

export const MySnippetsProvider = (props) => {
  const { children } = props;
  const [filterValues, setFilterValues] = useState(initialState.filterValues);
  const [loading, setLoading] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [isPreloaded, setPreloaded] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [mySnippets, setSnippets] = useState(initialState.mySnippets);
  const [filteredMySnippets, setFilteredMySnippets] = useState(
    initialState.filteredMySnippets
  );
  const client = useRestApiClient();

  const deleteMySnippet = async (snippetId) => {
    setRemoving(true);
    await client.delete(`/snippets/${snippetId}`);
    setSnippets((prev) => {
      const filtered = prev.filter((row) => row.id !== snippetId);
      return [...filtered];
    });
    setRemoving(false);
  };

  const loadMySnippets = async (force = false) => {
    if (mySnippets?.length > 0 && !force) return;
    if (loading) return;

    try {
      setLoading(true);
      const {
        data: { message }
      } = await client.get('/collections/snippets');
      setSnippets(message);
      // setFilteredMySnippets(message);
      setPreloaded(true);
    } catch (error) {
      setSnippets([]);
      if (error?.response?.status === 404) {
        setPreloaded(true);
      }
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const saveSnippet = async (payload) => {
    try {
      setSubmitting(true);
      const {
        data: { message: newSnippet }
      } = await client.post('/snippets', payload);

      setSnippets((currentList) => {
        return currentList ? [...currentList, newSnippet] : [newSnippet];
      });
      setPreloaded(true);
      setSubmitting(false);
    } catch (error) {
      setSubmitting(false);
      throw error;
    }
  };

  const updateSnippet = useCallback(
    async (snippetId, payload, isLocal = false) => {
      // update local copy of record
      if (isLocal) {
        setSnippets((currentList) => {
          const newList = currentList.map((snippet) => {
            return snippet.id === snippetId
              ? { ...snippet, ...payload }
              : snippet;
          });
          return [...newList];
        });
      } else {
        let result = {};
        try {
          setSubmitting(true);
          const {
            data: { message }
          } = await client.patch(`/snippets/${snippetId}`, payload);

          if (message.pending_approval) {
            delete payload.private;
          }

          result = message;
        } catch (error) {
          setSubmitting(false);
          throw error;
        }
        setSubmitting(false);

        return result;
      }

      return null;
    },
    [mySnippets]
  );

  const updateSnippetLocal = useCallback(
    (snippetId, data) => {
      setSnippets((currentList) => {
        const newList = currentList.map((snippet) => {
          return snippet.id === snippetId ? { ...snippet, ...data } : snippet;
        });
        return [...newList];
      });
    },
    [mySnippets]
  );

  useEffect(() => {
    let snippets = mySnippets || [];

    const { search_title: searchTitle } = filterValues;

    if (searchTitle) {
      gtagEvent('search', {
        search_term: searchTitle
      });

      snippets = snippets.filter(
        ({ description, title }) =>
          (description &&
            description.toLowerCase().indexOf(searchTitle.toLowerCase()) >
              -1) ||
          (title && title.toLowerCase().indexOf(searchTitle.toLowerCase()) > -1)
      );
    }

    setFilteredMySnippets([...snippets]);
  }, [mySnippets, filterValues]);

  const values = useMemo(
    () => ({
      isPreloaded,
      loading,
      removing,
      submitting,
      filterValues,
      mySnippets,
      filteredMySnippets,
      updateSnippetLocal,
      setFilterValues,
      loadMySnippets,
      saveSnippet,
      updateSnippet,
      deleteMySnippet
    }),
    [
      isPreloaded,
      loading,
      removing,
      submitting,
      filterValues,
      mySnippets,
      filteredMySnippets,
      updateSnippet,
      updateSnippetLocal
    ]
  );

  return (
    <MySnippetsContext.Provider value={values} {...props}>
      {children}
    </MySnippetsContext.Provider>
  );
};

MySnippetsProvider.propTypes = {
  children: PropTypes.node.isRequired
};

export default MySnippetsContext;
